import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from '@/components/ui/sheet';
import MoveLeftIcon from '@/components/icons/MoveLeftIcon';
import EAvatar from '@/components/everridge/EAvatar';
import PhoneIcon from '@/components/icons/PhoneIcon';
import CreateDetailsCopyItem from '@/features/owners/utils/CreateDetailsCopyItem';
import { Toaster } from 'sonner';
import { useOwnerDetailsApi } from '@/features/owners/hooks/useOwnerDetailsApi';
import Mail_02_Icon from '@/components/icons/Mail_02_Icon';
import OwnerDetailsList from '@/features/owners/components/OwnersDetails/OwnerDetailsList';
import IdentityCardIcon from '@/components/icons/IdentityCardIcon';
import { OwnersUtils } from '@/features/owners/utils/ownersUtils';
import { cn } from '@/lib/utils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import Cancel_01_Icon from '@/components/icons/Cancel_01_Icon';
import { ApiConfig } from '@/config/api.config';

type Props = {
	id?: string;
	onClose: (state: boolean) => void;
};

const OwnerDetails: FC<Props> = ({ id, onClose }) => {
	const [isOpen, setIsOpen] = useState(true);

	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const { t } = useTranslation();

	const { ownerDetailsData } = useOwnerDetailsApi({ id });

	if (!ownerDetailsData) return null;

	const { statusStyles } = OwnersUtils.getStatusData(
		ownerDetailsData?.status,
	);

	const handleClose = () => {
		setIsOpen(false);
		setTimeout(onClose, 300);
	};

	return (
		<Sheet
			open={isOpen}
			onOpenChange={handleClose}>
			<SheetContent
				className={cn('flex flex-col', isMobile ? 'w-full' : 'w-160')}>
				<SheetHeader>
					<SheetDescription />
					<SheetTitle>
						{!isMobile && (
							<SheetClose
								className={cn(
									'flex items-center justify-center cursor-pointer',
									isMobile &&
										'size-11 border border-popover-100 rounded-full',
								)}>
								<MoveLeftIcon />
							</SheetClose>
						)}
						{t('main.owners.components.details.title')}
						{isMobile && (
							<SheetClose className='size-11 ml-auto flex items-center justify-center cursor-pointer border border-popover-100 rounded-full'>
								<Cancel_01_Icon />
							</SheetClose>
						)}
					</SheetTitle>
				</SheetHeader>
				<div className='flex-1 flex flex-col bg-popover-30 overflow-hidden select-none'>
					<div
						className={cn(
							'h-full p-6 flex flex-col gap-6 overflow-y-scroll no-scrollbar',
							isMobile && 'p-4',
						)}>
						<div
							className={
								'py-10 px-6 flex flex-col items-center gap-4 rounded-2.5xl bg-popover-10 border border-popover-100'
							}>
							<div className='relative'>
								<EAvatar
									className={'size-16'}
									url={
										ownerDetailsData.avatarUrl
											? `${ApiConfig.currentUrls.api.slice(
													0,
													-1,
											  )}${ownerDetailsData.avatarUrl}`
											: undefined
									}
								/>
								<div
									className={cn(
										'absolute bottom-0 right-1 size-4 rounded-full border-3 border-popover-10',
										statusStyles,
									)}
								/>
							</div>
							<div className='text-center'>
								<h3
									className={cn(
										'text-lg font-medium',
										isMobile && 'text-base',
									)}>
									{ownerDetailsData.name}
								</h3>
								<p className={'text-sm text-popover-800'}>
									{ownerDetailsData.language}
								</p>
							</div>
							<div
								className={cn(
									'flex justify-center flex-wrap gap-2',
									isMobile && 'flex-col w-full',
								)}>
								<CreateDetailsCopyItem
									type='Email'
									className={cn(
										isMobile && 'justify-between',
									)}>
									<Mail_02_Icon />
									{ownerDetailsData.email}
								</CreateDetailsCopyItem>
								<CreateDetailsCopyItem
									type='Phone'
									className={cn(
										isMobile && 'justify-between',
									)}>
									<PhoneIcon />
									{ownerDetailsData.phoneNumber}
								</CreateDetailsCopyItem>
								<CreateDetailsCopyItem
									type='TIN'
									className={cn(
										isMobile && 'justify-between',
									)}>
									<IdentityCardIcon />
									{ownerDetailsData.tin ??
										t(
											'main.owners.components.details.noTIN',
										)}
								</CreateDetailsCopyItem>
							</div>
						</div>
						<OwnerDetailsList ownerDetailsData={ownerDetailsData} />
					</div>
				</div>
				<Toaster />
			</SheetContent>
		</Sheet>
	);
};

export default OwnerDetails;
